import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

export function CapabilityFold({
  title,
  hint,
  count,
  defaultOpen = false,
  children,
}: {
  title: string;
  hint?: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="group/fold rounded-xl border border-border">
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-3 py-2.5 text-left outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{title}</p>
          {hint ? <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{hint}</p> : null}
        </div>
        {count != null ? (
          <span className="shrink-0 tabular-nums text-[11px] text-muted-foreground">{count}</span>
        ) : null}
        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/fold:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-border">{children}</CollapsibleContent>
    </Collapsible>
  );
}

export function NestedFold({
  title,
  count,
  defaultOpen = false,
  children,
}: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="group/nested">
      <CollapsibleTrigger className="flex w-full items-center gap-2 px-2.5 py-2 text-left outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring">
        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/nested:rotate-180" />
        <span className="min-w-0 flex-1 text-xs font-medium">{title}</span>
        <span className="shrink-0 tabular-nums text-[11px] text-muted-foreground">{count}</span>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}

export type CapabilityBrandOption = {
  id: number;
  name: string;
};

export function BrandPickList({
  brands,
  activeId,
  disabled = false,
  busyId = null,
  emptyText,
  ariaLabel,
  onPick,
}: {
  brands: CapabilityBrandOption[];
  activeId: number | null;
  disabled?: boolean;
  busyId?: number | null;
  emptyText: string;
  ariaLabel: string;
  onPick: (brandId: number | null) => void;
}) {
  if (brands.length === 0) {
    return <p className="px-3 py-3 text-xs text-muted-foreground">{emptyText}</p>;
  }

  return (
    <ul className="divide-y divide-border" aria-label={ariaLabel}>
      {brands.map((brand) => {
        const on = activeId === brand.id;
        const busy = busyId === brand.id;
        return (
          <li key={brand.id} className="flex items-center justify-between gap-3 px-3 py-2">
            <span className="min-w-0 truncate text-sm">{brand.name}</span>
            <Switch
              size="sm"
              checked={on}
              disabled={disabled || busy}
              aria-label={brand.name}
              onCheckedChange={(checked) => onPick(checked ? brand.id : null)}
              className={cn(busy && "opacity-70")}
            />
          </li>
        );
      })}
    </ul>
  );
}
